//================================================================
//  Component: Browse
//================================================================

//  Purpose: Table that contains the users datasets


//  Properties:
//    - filesFiltered = {useState, holds the dataset documents}
//    - dataSetsFiltered = {useState, holds the filtered dataset documents}
//    - setFilesFiltered = {useState, holds the filtered dataset documents}


//  Example:
//      <Browse
//          filesFiltered={filesFiltered}
//          dataSetsFiltered={dataSetsFiltered}
//          setFilesFiltered={setFilesFiltered}
//      ></Browse>

//================================================================


//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

//Contexts
import { SetToast } from '../../Library/GlobalContexts';

//Functions
import QueryDocument from '../../Library/QueryDocument';
import ConvertDate from '../../Library/ConvertDate';
import DownloadFile from '../../Library/DownloadFile';

//Components
import PageComponent from '../../Components/PageComponent/PageComponent';
import BreadCrumb from './BreadCrumb/BreadCrumb';
import DownloadInput from './DownloadInput/DownloadInput';

//Images
import TableSorting from '../../Components/TableFiltersSorting/TableSorting';
import IconSearch from '../../Components/Images/Icon_Search_Grey.svg';
import IconLeftArrowBlack from '../../Components/Images/Icon_LeftArrow_Black.svg';
import IconLeftArrowGrey from '../../Components/Images/Icon_LeftArrow_Grey.svg';
import IconRightArrowBlack from '../../Components/Images/Icon_RightArrow_Black.svg';
import IconRightArrowGrey from '../../Components/Images/Icon_RightArrow_Grey.svg';


export default function Browse() {

    //------------------------------------------------------
    //  React Router & Contexts
    //------------------------------------------------------

    const params = useParams();
    const [searchParams] = useSearchParams();
    
    const setToast = useContext(SetToast);

    // page limit options
    const options = [
        { value: 1000, label: '1000' },
        { value: 2000, label: '2000' },
        { value: 3000, label: '3000' },
    ];

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to save the page status > 'pending', 'onload', 'success', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Holds the current files & filtered files
    const [files, setFiles] = useState([]);
    const [filesFiltered, setFilesFiltered] = useState([]);

    // Column sorting states
    const [searchInput, setSearchInput] = useState('');

    // Column sorting states
    const [currentSort, setCurrentSort] = useState({
        'column': 'Name',
        'sort': 'descending',
    });

    // Files the user wants to download
    const [filesToDownload, setFilesToDownload] = useState([]);

    // Set Page Limit (this show how many items per page)
    const [pageLimit, setPageLimit] = useState(options[0].value); 

    // Array contain first and last item of each page, plus page count
    const [firstLastPage, setFirstLastPage] = useState([]);

    // Pointer to store current page location
    const [currentPageIndex, setCurrentPageIndex] = useState(1);

    // Used to store number of item on last page
    const [lastPageItems, setLastPageItems] = useState(0);

    // Used to store if it is the last page
    const [atLastPage, setAtLastPage] = useState(false);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Search for a filename
    function handleSearch(value) {

        setSearchInput(value);

        if (value.length === 0) {

            return setFilesFiltered([...files]);

        }

        const results = filesFiltered.filter((object) =>
            object?.filename?.toLowerCase().includes(value.toLowerCase())
        );

        const options = [];
        results.forEach((object) => {

            options.push(object.application);

        });

        setFilesFiltered(results);

    }

    // Requests the signed URL from Firebase GCS
    function handleDownload() {

        if (filesToDownload.length === 0) return;

        // Open the toast
        setToast({
            'type': 'pending', // info, pending, success, warning, error
            'message': 'Preparing for download',
        });

        const downloadPromises = [];

        // Loop through each file and create a downloadable link
        filesToDownload.forEach((row) => {

            downloadPromises.push(
                DownloadFile(`gs://${params.id}${row.folderpath}${row.filename}`),
                row?.contenttype
            );
            
        });

        // Open each link in a new tab
        Promise.all(downloadPromises).then((items) => {
            items = splitArrayIntoChunks(items, 2);

            items.forEach((item, index) => {

                const url = item[0];
                const contenttype = item[1];
                
                // Image file, pdf file, text file and Excel spreadsheet content types
                if (/xls|ppt|doc/.test(filesToDownload[index]?.filename.split('.').pop()) ||
                    contenttype.includes('image') || 
                    contenttype.includes('pdf') || 
                    contenttype.includes('text') ||
                    contenttype.includes('application/vnd')) {

                    fetch(url).then((blob) => {
                        blob.blob().then((blobObj) => {

                            // Create url from file object
                            const url = URL.createObjectURL(blobObj);
    
                            // Create new Element -> Add the file name & URL
                            const a = document.createElement('a');
                            a.download = filesToDownload[index]?.filename === undefined ?
                                            'noname' :
                                            filesToDownload[index]?.filename;
                            a.href = url;
    
                            // Trigger download & Cleanup
                            a.click();
    
                        })
                    })

                    return;

                } 
                
                // All other file content types

                // Create new Element -> Add the file name & URL
                const a = document.createElement('a');
                a.download = filesToDownload[index]?.filename === undefined ?
                                'noname' :
                                filesToDownload[index]?.filename;
                a.href = url;

                // Trigger download & Cleanup
                a.click();
                
      
            });

            setFilesToDownload([]);

            // Close toast
            setToast({
                'type': '',
                'message': '',
            });

        }).catch((error) => {
    
            console.log(error);

            // Set error on toast
            setToast({
                'type': 'error', // info, pending, success, warning, error
                'message': 'Failed to prepare link(s)',
            });

        });

    }

    function splitArrayIntoChunks(arr, chunkSize) {
        let resultArray = [];
        for (let i = 0; i < arr.length; i += chunkSize) {
            resultArray.push(arr.slice(i, i + chunkSize));
        }
        return resultArray;
    }

    // Handle browsing to previous page
    // - currentPageIndex is the current page number
    // - atLastPage flag whether current page is the last page
    // - lastPageItems store the number of items of last page that is less than page limit number
    function handlePrevPage() {

        const dataSetId = params.id;

        if (dataSetId === '') return;
        if (dataSetId === null) return;

        if (firstLastPage[currentPageIndex - 2]?.firstItem === '') return;
        if (firstLastPage[currentPageIndex - 2]?.firstItem === null) return;
        if (firstLastPage[currentPageIndex - 2]?.firstItem === undefined) return;
      
        QueryDocument('files', [
            ['datasetid', '==', dataSetId],
            ['folderpath', '==', searchParams.get('path')],
        ], ['filename', 'desc'], pageLimit, firstLastPage[currentPageIndex - 2].firstItem, undefined).then((documents) => {
            
            setFiles(documents);
            setFilesFiltered(documents);

            setCurrentPageIndex(currentPageIndex - 1);
            setLastPageItems(documents.length);
            setAtLastPage(false);

            // Close toast
            setToast({
                'type': '',
                'message': '',
            });

        }).catch((error) => {

            console.log(error);
            setPageStatus('error-fatal');

            // Set error on toast
            setToast({
                'type': 'error', // info, pending, success, warning, error
                'message': 'Failed to prepare next/previous page',
            });


        });


    }

    // Handle browsing to next page
    // - firstLastPage record the page number, first item file id, 
    //   and last item file id for each page as it browse to the 
    //   next page so it can navigate back to the previous page
    function handleNextPage() {

        const dataSetId = params.id;

        if (dataSetId === '') return;
        if (dataSetId === null) return;     

        if (files[pageLimit - 1]?.filename === '') return;
        if (files[pageLimit - 1]?.filename === null) return;
        if (files[pageLimit - 1]?.filename === undefined) return;
        
        QueryDocument('files', [
            ['datasetid', '==', dataSetId],
            ['folderpath', '==', searchParams.get('path')],
        ], ['filename', 'desc'], pageLimit, undefined, files[pageLimit - 1]?.filename).then((documents) => {
            
            firstLastPage.push({  
                pageIndex: currentPageIndex,               
                firstItem: files[0]?.filename,                // first file item of current page
                lastItem: files[pageLimit - 1]?.filename,     // last file item on current page
            })

            setFirstLastPage([...new Map(firstLastPage.map(item => [JSON.stringify(item), item])).values()].sort((a, b) => a.pageIndex - b.pageIndex));

            setCurrentPageIndex(currentPageIndex + 1);
            setAtLastPage(false);
            
            setFiles(documents);
            setFilesFiltered(documents);

            if (documents.length < pageLimit && !atLastPage) {
                setAtLastPage(true);
                setLastPageItems(documents.length);
                return;
            }

            
            // Close toast
            setToast({
                'type': '',
                'message': '',
            });

        }).catch((error) => {

            console.log(error);
            setPageStatus('error-fatal');

            // Set error on toast
            setToast({
                'type': 'error', // info, pending, success, warning, error
                'message': 'Failed to prepare next/previous page',
            });

        });
        
        

    }

    // Set page limit 
    // - set pageLimit state for how many items in one page, 
    //   update current page to first page documents
    function handlePageLimit(pageLimitSelection) {

        const pageLimit = Number(pageLimitSelection);

        const dataSetId = params.id;

        if (dataSetId === '') return;
        if (dataSetId === null) return;

        QueryDocument('files', [
            ['datasetid', '==', dataSetId],
            ['folderpath', '==', searchParams.get('path')],
        ], ['filename', 'desc'], pageLimit).then((documents) => {

            setPageLimit(Number(pageLimitSelection));
            setFirstLastPage([]);
            setCurrentPageIndex(1);
            setLastPageItems(0);
            setAtLastPage(false);

            setFiles(documents);
            setFilesFiltered(documents);
            setPageStatus('onload');

        }).catch((error) => {

            console.log(error);
            setPageStatus('error-fatal');

        }); 
        
    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // onLoad
    //  - Datset is set once, so we need the list of 'application' list
    useEffect(() => {

        const dataSetId = params.id;

        if (dataSetId === '') return;
        if (dataSetId === null) return;

        // Reset pagination states
        setFirstLastPage([]);
        setCurrentPageIndex(1);
        setLastPageItems(0);
        setAtLastPage(false);

        QueryDocument('files', [
            ['datasetid', '==', dataSetId],
            ['folderpath', '==', searchParams.get('path')],
        ], ['filename', 'desc'], pageLimit).then((documents) => {

            setFiles(documents);
            setFilesFiltered(documents);
            setPageStatus('onload');

        }).catch((error) => {

            console.log(error);
            setPageStatus('error-fatal');

        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);


    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (

        <PageComponent
            status={pageStatus}
            body={

                <div className='Table-Container'>

                    <BreadCrumb></BreadCrumb>

                    {/* ======== Sorting and Filters ========== */}
                    <div className='flex flex-row justify-end py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                        <div className='flex flex-row gap-2'>

                            <button className='Primary-Button' style={{margin: '0px'}} disabled={filesToDownload.length === 0 ? true : false} onClick={() => handleDownload()}>
                                Prepare Download
                            </button>

                            <div className='flex flex-row gap-2'>

                                {/* ======== Search Bar ======== */}
                                <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[300px]'>
                                    <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                                    <label htmlFor='searchInput'>
                                        <input
                                            id='searchInput'
                                            className='border-none h-[37px] p-0 m-0 outline-none bg-white'
                                            type='text'
                                            placeholder='Search'
                                            onChange={(e) => handleSearch(e.target.value)}
                                            value={searchInput}
                                        ></input>
                                    </label>
                                </div>

                            </div>

                        </div>

                    </div>

                    <table className='w-full max-h-96 overflow-y-scroll'>
                        <thead>
                            <tr className='border-b-[#D8D8D8] border-solid border-b-2'>

                                <TableSorting
                                    column='Name'
                                    currentSort={currentSort}
                                    setCurrentSort={setCurrentSort}
                                    filteredTableArray={filesFiltered}
                                    setFilteredTableArray={setFilesFiltered}
                                    objectKey='filename'
                                ></TableSorting>
                                <TableSorting
                                    column='Last Updated'
                                    currentSort={currentSort}
                                    setCurrentSort={setCurrentSort}
                                    filteredTableArray={filesFiltered}
                                    setFilteredTableArray={setFilesFiltered}
                                    objectKey='lastupdated'
                                ></TableSorting>
                                <TableSorting
                                    column='Created'
                                    currentSort={currentSort}
                                    setCurrentSort={setCurrentSort}
                                    filteredTableArray={filesFiltered}
                                    setFilteredTableArray={setFilesFiltered}
                                    objectKey='created'
                                ></TableSorting>
                                <TableSorting
                                    column='Size (MB)'
                                    currentSort={currentSort}
                                    setCurrentSort={setCurrentSort}
                                    filteredTableArray={filesFiltered}
                                    setFilteredTableArray={setFilesFiltered}
                                    objectKey='filesize'
                                ></TableSorting>
                                <TableSorting
                                    column='Type'
                                    currentSort={currentSort}
                                    setCurrentSort={setCurrentSort}
                                    filteredTableArray={filesFiltered}
                                    setFilteredTableArray={setFilesFiltered}
                                    objectKey='contenttype'
                                ></TableSorting>

                            </tr>
                        </thead>
                        <tbody>
                            {
                                // No files found
                                filesFiltered.length === 0 ?
                                    (
                                        <tr>
                                            <td colSpan={7}>
                                                <div className='text-center p-[2%]'>
                                                    No files found.
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                    :
                                    (
                                        // Map in each file/folder
                                        filesFiltered.map((row, index) => (
                                            <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0'>
                                                <td className='py-[15px] px-[20px] font-medium'>
                                                    <DownloadInput
                                                        row={row}
                                                        filesToDownload={filesToDownload}
                                                        setFilesToDownload={setFilesToDownload}
                                                    ></DownloadInput>
                                                </td>
                                                <td className='py-[15px] px-[20px]'>{ConvertDate(row?.lastupdated)}</td>
                                                <td className='py-[15px] px-[20px]'>{ConvertDate(row?.created)}</td>
                                                <td className='py-[15px] px-[20px]'>{row?.filesize}</td>
                                                <td className='py-[15px] px-[20px]'>
                                                    {
                                                        // Don't show the folder type
                                                        row?.contenttype === 'folder' ?
                                                            '-'
                                                            :
                                                            row?.contenttype
                                                    }
                                                </td>
                                            </tr>
                                        ))
                                    )
                            }
                        </tbody>
                    </table>

                    {/* Pagination Container */}
                    <div className='flex flex-row-reverse gap-2'>

                        <select
                            className='border border-gray-300 border-spacing-x-100 border-spacing-y-100 rounded-md'
                            defaultValue={pageLimit}
                            onChange={(e) => handlePageLimit(e.target.value)}
                        >
                            {options.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label} per page
                                </option>
                            ))}
                        </select>
                        
                        {/* Container | Showing 1 - 1000 */}
                        <div className='justify-end'>

                            <div className='flex flex-row border rounded-md p-[2px]'>

                                <label className='pl-2 pr-2 border-r border-solid'>
                                    Showing {((currentPageIndex - 1) * pageLimit) + 1} - {((currentPageIndex - 1) * pageLimit) + (atLastPage ? lastPageItems : pageLimit)}
                                </label>

                                {/* Forward */}
                                {
                                    currentPageIndex === 1 ? 
                                        <img className='ml-[10px]' src={IconLeftArrowGrey} alt='previousPage-Grey-Icon' onClick={handlePrevPage}></img>
                                    :
                                        <img className='ml-[10px] cursor-pointer' src={IconLeftArrowBlack} alt='previousPage-Black-Icon' onClick={handlePrevPage}></img>
                                
                                }

                                {/* Backwards */}
                                {
                                    atLastPage ? 
                                        <img className='ml-[10px]' src={IconRightArrowGrey} alt='nextPage-Grey-Icon' onClick={handleNextPage}></img>
                                    :
                                        <img className='ml-[10px] cursor-pointer' src={IconRightArrowBlack} alt='nextPage-Black-Icon' onClick={handleNextPage}></img>
                                
                                }

                            </div>

                        </div>

                    </div>

                </div>

            }
        ></PageComponent>

    )

}
